import React from "react";
import { IconButton } from "../../atoms/IconButton";
import { ChevronDownIcon } from "../../atoms/ChevronDownIcon";
import { StripRoot, Track, VerticalRoot } from "./SlideThumbnailStrip.styles";
import type { SlideThumbnailStripProps } from "./SlideThumbnailStrip.types";

/* IconButton expects an icon component taking { size }; fix the direction here. */
const ChevronLeftIcon: React.FC<{ size?: number }> = ({ size }) => (
  <ChevronDownIcon size={size} direction="left" />
);
const ChevronRightIcon: React.FC<{ size?: number }> = ({ size }) => (
  <ChevronDownIcon size={size} direction="right" />
);

/**
 * Present-mode filmstrip: a shrink-to-content bordered strip of
 * SlideThumbnail cards. Chevrons render only when the parent says there is
 * more content in that direction (canPrev/canNext) — the Figma at-rest
 * state has none. Stateless — overflow detection, the active slide and
 * prev/next semantics all live in the parent organism.
 */
export const SlideThumbnailStrip: React.FC<SlideThumbnailStripProps> = ({
  children,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  ariaLabel = "Slide navigation",
  trackRef,
  orientation = "horizontal",
  className,
}) =>
  orientation === "vertical" ? (
    <VerticalRoot
      role="toolbar"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </VerticalRoot>
  ) : (
    <StripRoot role="toolbar" aria-label={ariaLabel} className={className}>
      {canPrev && (
        <IconButton
          icon={ChevronLeftIcon}
          aria-label="Previous slide"
          onClick={onPrev}
        />
      )}
      <Track ref={trackRef}>{children}</Track>
      {canNext && (
        <IconButton
          icon={ChevronRightIcon}
          aria-label="Next slide"
          onClick={onNext}
        />
      )}
    </StripRoot>
  );

export default SlideThumbnailStrip;
